import { action, observable, makeObservable } from 'mobx';
import { type IkeClientService, Services } from './index';

const MAP_TYPE_KEY_PREFIX = 'mapTypeId:';

/**
 * Set on the attached map wrapper once Google Maps has loaded tiles and gone idle (projection ready).
 * `isReady` only means the Maps *API script* loaded; this means the *instance* is interactive, so a
 * pixel<->latLng click resolves correctly. E2E gates map readiness on it instead of a fixed wait. See UFO-743.
 */
const MAP_IDLE_ATTR = 'data-map-idle';

const MAP_OPTIONS: google.maps.MapOptions = {
	/* Disable clicking points of interest. This makes it confusing when trying to click on collections. */
	clickableIcons: false,
	/* Disable 45° camera angle in satellite map mode. Only top-down angle will position collections accurately. */
	tilt: 0,
	/* Feature works but is not tested. Disable until asked for. */
	rotateControl: false,
};

export class MapService implements IkeClientService {
	__priority = 100;

	private mapInstance?: google.maps.Map;

	/** The wrapper the map is currently attached to; flagged idle once the instance settles. */
	private idleWrapper?: HTMLElement;

	@observable isReady = false;

	/** Promise for when google maps is ready */
	isReadyPromise!: Promise<void>;

	constructor() {
		makeObservable(this);
	}

	init() {
		this.isReadyPromise = new Promise((resolve) => {
			const waitForGoogleMaps = Services.Window.window.setInterval(() => {
				if (typeof google === 'undefined') {
					return;
				}
				Services.Window.window.clearTimeout(waitForGoogleMaps);
				this.setIsReady();
				resolve();
			}, 10);
		});
	}

	/**
	 * Fetch Google Map instance, reusing existing instance to avoid impacting Google Maps API quota.
	 */
	get map(): google.maps.Map {
		if (!this.isReady) {
			throw new Error('Cannot fetch map before Google Maps API loaded');
		}

		if (this.mapInstance == null) {
			const mapEl = Services.Window.document.createElement('div');
			mapEl.style.width = '100%';
			mapEl.style.height = '100%';

			this.mapInstance = new google.maps.Map(mapEl, MAP_OPTIONS);
			// Flag the current wrapper idle each time the map settles (tiles loaded, projection ready).
			this.mapInstance.addListener('idle', () => this.idleWrapper?.setAttribute(MAP_IDLE_ATTR, 'true'));
		}

		return this.mapInstance;
	}

	attach(el: HTMLElement): google.maps.Map {
		const map = this.map;
		if (map == null) {
			return null;
		}

		el.appendChild(map.getDiv());
		// New mount: not idle until the map settles again (the idle listener above re-flags it).
		el.removeAttribute(MAP_IDLE_ATTR);
		this.idleWrapper = el;
		// The map instance is reused across pages, so it may already be loaded and won't re-fire `idle` on
		// its own when moved into a new container, and a first-load `idle` can fire before idleWrapper is
		// set. Nudge it to re-render here (google.maps preserves center across resize) so the idle listener
		// reliably flags the current wrapper. A genuine browser-window resize is unaffected by this: it
		// fires the map's own resize -> idle independently, which only re-sets MAP_IDLE_ATTR to 'true' on
		// the current wrapper (idempotent), and that attribute is read solely by the Cypress suite, never
		// by production code. See UFO-743.
		google.maps.event.trigger(map, 'resize');
		return map;
	}

	@action setIsReady() {
		this.isReady = true;
	}

	getMapType(context: string, defaultType: string): string {
		return Services.LocalStorage.getItem<string>(MAP_TYPE_KEY_PREFIX + context) || defaultType;
	}

	listenMapType(context: string): google.maps.MapsEventListener {
		return this.map.addListener('maptypeid_changed', () => {
			const mapTypeId = this.map.getMapTypeId();
			Services.LocalStorage.setItem(MAP_TYPE_KEY_PREFIX + context, mapTypeId);
		});
	}
}
